import React, { useState, useEffect } from 'react';
import { 
  Target,
  TrendingUp,
  Users,
  Heart,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  Star,
  Award,
  Zap,
  Shield,
  Smile,
  Coffee,
  Sun,
  Moon,
  BarChart3,
  Activity,
  Sparkles,
  ChevronRight,
  Trophy,
  Flame
} from 'lucide-react';

interface OperatorMetrics {
  guestSatisfaction: number;
  operationalEfficiency: number;
  revenueOptimization: number;
  teamHarmony: number;
  personalWellbeing: number;
}

interface DailyFlow {
  time: string;
  task: string;
  status: 'done' | 'current' | 'upcoming';
  automated: boolean;
}

interface MasteryMilestone {
  id: string;
  title: string;
  description: string;
  progress: number;
  reward: string;
  icon: React.ReactNode;
}

const OperatorMastery: React.FC = () => {
  const [activeView, setActiveView] = useState<'flow' | 'metrics' | 'team' | 'growth'>('flow');
  const [timeOfDay, setTimeOfDay] = useState<'morning' | 'afternoon' | 'evening'>('morning');
  const [metrics, setMetrics] = useState<OperatorMetrics>({
    guestSatisfaction: 94,
    operationalEfficiency: 87,
    revenueOptimization: 78,
    teamHarmony: 91,
    personalWellbeing: 72
  });
  const [streakDays, setStreakDays] = useState(12);

  // Update time of day
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setTimeOfDay('morning');
    else if (hour < 18) setTimeOfDay('afternoon');
    else setTimeOfDay('evening');
  }, []);

  const dailyFlow: DailyFlow[] = [
    { time: '6:00', task: 'Coffee service starts', status: 'done', automated: true },
    { time: '6:30', task: 'Morning report review', status: 'done', automated: false },
    { time: '7:00', task: 'Team briefing', status: 'done', automated: false },
    { time: '8:00', task: 'Guest breakfasts', status: 'current', automated: false },
    { time: '10:00', task: 'Room turnovers', status: 'upcoming', automated: false },
    { time: '14:00', task: 'Check-ins preparation', status: 'upcoming', automated: true },
    { time: '16:00', task: 'Inventory check', status: 'upcoming', automated: true },
    { time: '18:00', task: 'Evening ambiance', status: 'upcoming', automated: false }
  ];

  const masteryMilestones: MasteryMilestone[] = [
    {
      id: '1',
      title: 'Flow Master',
      description: 'Complete 30 perfect days',
      progress: 73,
      reward: 'Unlock AI auto-pilot mode',
      icon: <Zap className="w-5 h-5" />
    },
    {
      id: '2',
      title: 'Guest Whisperer',
      description: '50 five-star reviews',
      progress: 86,
      reward: 'Premium guest insights',
      icon: <Heart className="w-5 h-5" />
    },
    {
      id: '3',
      title: 'Team Leader',
      description: 'Zero staff turnover for 6 months',
      progress: 60,
      reward: 'Advanced scheduling AI',
      icon: <Users className="w-5 h-5" />
    },
    {
      id: '4',
      title: 'Revenue Optimizer',
      description: 'Beat projections 3 months straight',
      progress: 45,
      reward: 'Dynamic pricing engine',
      icon: <TrendingUp className="w-5 h-5" />
    }
  ];

  const getTimeIcon = () => {
    switch(timeOfDay) {
      case 'morning': return <Sun className="w-6 h-6 text-yellow-400" />;
      case 'afternoon': return <Sun className="w-6 h-6 text-orange-400" />;
      case 'evening': return <Moon className="w-6 h-6 text-blue-400" />;
    }
  };

  const getMetricColor = (value: number) => {
    if (value >= 90) return 'text-green-400';
    if (value >= 70) return 'text-yellow-400';
    return 'text-red-400';
  };

  const renderFlowView = () => (
    <div className="space-y-6">
      {/* Daily Streak */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Flame className="w-6 h-6" />
              {streakDays} Day Streak!
            </h2>
            <p className="text-sm opacity-90 mt-1">Everything flowing perfectly</p>
          </div>
          <Trophy className="w-8 h-8 text-yellow-300" />
        </div>
      </div>

      {/* Current Status */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            {getTimeIcon()}
            {timeOfDay.charAt(0).toUpperCase() + timeOfDay.slice(1)} Flow
          </h3>
          <span className="text-xs bg-green-500/20 text-green-400 px-3 py-1 rounded-full">
            All systems optimal
          </span>
        </div>

        <div className="space-y-3">
          {dailyFlow.map((item, index) => (
            <div key={index} className={`flex items-center gap-3 ${item.status === 'done' && 'opacity-50'}`}>
              <div className="text-xs text-gray-500 w-12">{item.time}</div>
              <div className="flex-1 flex items-center gap-3">
                {item.status === 'done' ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : item.status === 'current' ? (
                  <div className="w-5 h-5 rounded-full bg-blue-500 animate-pulse" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-600" />
                )}
                <span className={`text-sm ${item.status === 'current' ? 'text-white font-semibold' : 'text-gray-300'}`}>
                  {item.task}
                </span>
                {item.automated && (
                  <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                    Auto
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4">Quick Actions</h3>
        
        <div className="grid grid-cols-2 gap-3">
          <button className="bg-gray-700 rounded-lg p-3 flex flex-col items-center gap-2">
            <Coffee className="w-6 h-6 text-blue-400" />
            <span className="text-xs text-gray-300">Order Supplies</span>
          </button>
          <button className="bg-gray-700 rounded-lg p-3 flex flex-col items-center gap-2">
            <Users className="w-6 h-6 text-green-400" />
            <span className="text-xs text-gray-300">Team Chat</span>
          </button>
          <button className="bg-gray-700 rounded-lg p-3 flex flex-col items-center gap-2">
            <Activity className="w-6 h-6 text-purple-400" />
            <span className="text-xs text-gray-300">View Analytics</span>
          </button>
          <button className="bg-gray-700 rounded-lg p-3 flex flex-col items-center gap-2">
            <Shield className="w-6 h-6 text-orange-400" />
            <span className="text-xs text-gray-300">Emergency</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderMetricsView = () => (
    <div className="space-y-6">
      {/* Master Score */}
      <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl p-6 text-white">
        <h2 className="text-xl font-bold mb-4">Mastery Score</h2>
        <div className="text-5xl font-bold mb-2">
          {Math.round((metrics.guestSatisfaction + metrics.operationalEfficiency + metrics.revenueOptimization + metrics.teamHarmony + metrics.personalWellbeing) / 5)}
        </div>
        <div className="text-sm opacity-90">Top 5% of operators globally</div>
      </div>

      {/* Detailed Metrics */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4">Performance Breakdown</h3>
        
        <div className="space-y-4">
          {[
            { label: 'Guest Satisfaction', value: metrics.guestSatisfaction, icon: <Heart className="w-5 h-5" /> },
            { label: 'Operational Efficiency', value: metrics.operationalEfficiency, icon: <Zap className="w-5 h-5" /> },
            { label: 'Revenue Optimization', value: metrics.revenueOptimization, icon: <DollarSign className="w-5 h-5" /> },
            { label: 'Team Harmony', value: metrics.teamHarmony, icon: <Users className="w-5 h-5" /> },
            { label: 'Personal Wellbeing', value: metrics.personalWellbeing, icon: <Smile className="w-5 h-5" /> }
          ].map((metric, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={getMetricColor(metric.value)}>{metric.icon}</span>
                  <span className="text-sm text-gray-300">{metric.label}</span>
                </div>
                <span className={`text-sm font-bold ${getMetricColor(metric.value)}`}>
                  {metric.value}%
                </span>
              </div>
              <div className="bg-gray-700 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    metric.value >= 90 ? 'bg-green-400' : 
                    metric.value >= 70 ? 'bg-yellow-400' : 'bg-red-400'
                  }`}
                  style={{ width: `${metric.value}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Insights */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-yellow-400" />
          AI Insights
        </h3>
        
        <div className="space-y-3">
          <div className="bg-gray-700 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5" />
              <div>
                <div className="text-sm text-white font-semibold">Efficiency Opportunity</div>
                <div className="text-xs text-gray-400">Automate morning inventory to save 30 min/day</div>
              </div>
            </div>
          </div>

          <div className="bg-gray-700 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <TrendingUp className="w-4 h-4 text-green-400 mt-0.5" />
              <div>
                <div className="text-sm text-white font-semibold">Revenue Boost</div>
                <div className="text-xs text-gray-400">Weekend rates could increase 15% based on demand</div>
              </div>
            </div>
          </div>

          <div className="bg-gray-700 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Heart className="w-4 h-4 text-red-400 mt-0.5" />
              <div>
                <div className="text-sm text-white font-semibold">Wellbeing Alert</div>
                <div className="text-xs text-gray-400">Schedule a day off - you've worked 12 days straight</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTeamView = () => (
    <div className="space-y-6">
      {/* Team Pulse */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl p-6 text-white">
        <h2 className="text-xl font-bold mb-3">Team Harmony</h2>
        <div className="flex items-center gap-4">
          <div className="text-4xl font-bold">91%</div>
          <div className="text-sm opacity-90">
            <div>6 team members</div>
            <div>All happy & productive</div>
          </div>
        </div>
      </div>

      {/* Team Status */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4">Today's Team</h3>
        
        <div className="space-y-3">
          {[
            { name: 'Ana', role: 'Reception', status: 'active', mood: 'great', tasks: 3 },
            { name: 'Carlos', role: 'Maintenance', status: 'active', mood: 'good', tasks: 5 },
            { name: 'Maria', role: 'Housekeeping', status: 'active', mood: 'great', tasks: 8 },
            { name: 'João', role: 'Kitchen', status: 'break', mood: 'good', tasks: 2 }
          ].map((member, index) => (
            <div key={index} className="bg-gray-700 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                  member.status === 'active' ? 'bg-green-500' : 'bg-gray-500'
                }`}>
                  {member.name[0]}
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">{member.name}</div>
                  <div className="text-xs text-gray-400">{member.role}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-xs text-gray-400">{member.tasks} tasks</div>
                  <div className="text-xs">
                    {member.mood === 'great' ? '😊' : '🙂'} {member.mood}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Team Achievements */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4">Team Achievements</h3>
        
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-700 rounded-lg p-3 text-center">
            <Award className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
            <div className="text-xs text-gray-300">Perfect Week</div>
            <div className="text-sm font-bold text-white mt-1">2 in a row</div>
          </div>
          <div className="bg-gray-700 rounded-lg p-3 text-center">
            <Star className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <div className="text-xs text-gray-300">Guest Praise</div>
            <div className="text-sm font-bold text-white mt-1">12 this month</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGrowthView = () => (
    <div className="space-y-6">
      {/* Mastery Path */}
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-2xl p-6 text-white">
        <h2 className="text-xl font-bold mb-4">Your Growth Journey</h2>
        <div className="text-sm opacity-90 mb-3">Pousada Master • Level 7</div>
        <div className="bg-white/20 rounded-full h-3">
          <div className="bg-white rounded-full h-3" style={{ width: '73%' }} />
        </div>
        <div className="text-xs mt-2 opacity-80">1,850 XP to Level 8</div>
      </div>

      {/* Milestones */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4">Mastery Milestones</h3>
        
        <div className="space-y-3">
          {masteryMilestones.map(milestone => (
            <div key={milestone.id} className="bg-gray-700 rounded-lg p-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400">
                  {milestone.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-white">{milestone.title}</span>
                    <span className="text-xs text-gray-400">{milestone.progress}%</span>
                  </div>
                  <div className="text-xs text-gray-400 mb-2">{milestone.description}</div>
                  <div className="bg-gray-600 rounded-full h-1.5 mb-2">
                    <div 
                      className="bg-purple-400 rounded-full h-1.5"
                      style={{ width: `${milestone.progress}%` }}
                    />
                  </div>
                  <div className="text-xs text-blue-400">Reward: {milestone.reward}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Learning Opportunities */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4">Next Skills to Master</h3>
        
        <div className="space-y-3">
          <div className="bg-gray-700 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              <div>
                <div className="text-sm text-white">Revenue Management Pro</div>
                <div className="text-xs text-gray-400">Dynamic pricing strategies</div>
              </div>
            </div>
            <button className="text-xs bg-blue-600 text-white px-3 py-1 rounded">
              Start
            </button>
          </div>

          <div className="bg-gray-700 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Heart className="w-5 h-5 text-red-400" />
              <div>
                <div className="text-sm text-white">Guest Psychology</div>
                <div className="text-xs text-gray-400">Anticipate needs before asked</div>
              </div>
            </div>
            <button className="text-xs bg-gray-600 text-white px-3 py-1 rounded">
              Locked
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900">
      {/* Header */}
      <div className="bg-gray-900/80 backdrop-blur-lg border-b border-gray-800 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target className="w-6 h-6 text-purple-400" />
            <div>
              <h1 className="text-lg font-bold text-white">Operator Mastery</h1>
              <p className="text-xs text-gray-400">Level 7 • Master Operator</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Flame className="w-4 h-4 text-orange-400" />
            <span className="text-xs text-orange-400 font-bold">{streakDays}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-4 pb-20">
        {activeView === 'flow' && renderFlowView()}
        {activeView === 'metrics' && renderMetricsView()}
        {activeView === 'team' && renderTeamView()}
        {activeView === 'growth' && renderGrowthView()}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-lg border-t border-gray-800">
        <div className="grid grid-cols-4 py-2">
          {[
            { id: 'flow', icon: Clock, label: 'Flow' },
            { id: 'metrics', icon: BarChart3, label: 'Metrics' },
            { id: 'team', icon: Users, label: 'Team' },
            { id: 'growth', icon: TrendingUp, label: 'Growth' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id as any)}
              className={`flex flex-col items-center gap-1 py-2 ${
                activeView === tab.id ? 'text-purple-400' : 'text-gray-400'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span className="text-xs">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OperatorMastery;